import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import AthleteEditForm from "./AthleteEditForm";
import TermsAndConditionsModal from "./TermsAndConditionsModal";
import ProfileAvatar from "./ProfileAvatar";
import ShareProfileModal from "./ShareProfileModal";
import GuardianAthleteSelector from "./GuardianAthleteSelector";
import AthleteDeleteAccountModal from "./AthleteDeleteAccountModal";
import PasswordForm from "./PasswordForm";
import AddAthleteProfileModal from "./AddAthleteProfileModal";
import AthleteTeamLeadSection from "./AthleteTeamLeadSection";
import { isAthlete, getTermsAccepted, isGuardian, isAdmin, isStaff, isCoach, isPolaris } from "../utils/auth";
import { athleteProfileFetchPath, canAthleteSelfEditProfile, getGuardianViewingAthleteId } from "../utils/viewedAthlete";
import { buildApiUrl } from "../utils/apiConfig";
import { PROFILE_SHARING_UI_ENABLED } from "../utils/featureFlags";
import { SHOW_ATHLETE_PROFILE_ZIP_FIELD } from "../constants/athleteProfileFeatures";
import {
    autoFormatDateInput,
    formatDateOfBirthUs,
    formatLastLoginDateTime,
    isIsoDateStrictlyBeforeToday,
    parseDateOfBirthInputToIso,
} from "../utils/dateDisplay";
import { updateAthleteProfile } from "../utils/athleteApi";
import { useMyAvatar } from "../utils/profilePictureUtils";
import { useAuth } from "../contexts/AuthContext";
import { logoutAfterPasswordChange } from "../utils/passwordChangeLogout";
import {
    validateInstagramHandle,
    validateTikTokHandle,
    validateLinkedInUrl,
    validateFacebookName,
    validateHudlUrl,
    validateXHandle,
    formatAtHandleDisplay,
    normalizeAtHandlesInSocials,
    getInstagramUrl,
    getTikTokUrl,
    getLinkedInUrl,
    getFacebookUrl,
    getHudlUrl,
    getXUrl
} from "../utils/socialMediaUtils";
import "../styles/athlete-admin-layout.css";
import {
    AthleteAdminBreadcrumbNav,
    AthleteAdminNavState,
    getTeamMemberBreadcrumbLabel,
    navigateToTeamMemberFromBreadcrumb,
    useAdminAthleteDisplayName,
    useAthleteStaffForAdminBreadcrumb,
} from "./AthleteAdminBreadcrumbNav";
import "../styles/athlete-profile.css";
import "../styles/athlete-edit-form.css";
import "../styles/athlete-admin-page-spacing.css";
import EditIcon from "../images/buttons/Edit.svg";
import { apiClient } from "../utils/apiClient";


interface Sport {
    id: number;
    name: string;
}

interface AthleteProfileData {
    id?: string;
    userId?: number;  // The actual user ID for API calls (different from profile id)
    firstName?: string;
    lastName?: string;
    nickname?: string;
    sport?: Sport | string;  // Can be Sport object or legacy string
    graduationYear?: string;
    school?: string;
    phone?: string;
    email?: string;
    parentEmail?: string;
    parentEmail2?: string;
    parentPhone?: string;
    dateOfBirth?: string;
    program?: string;
    location?: string;
    pronouns?: string;
    profileImageUrl?: string;
    imageUrl?: string;  // Some API endpoints return imageUrl instead
    hasSignedIn?: boolean;
    athletePhone?: string;
    parentName?: string;
    parentMobile?: string;
    stateId?: number;
    staffName?: string;
    /** User id of assigned team lead (for admin profile fetch / breadcrumb). */
    staffId?: number;
    /** StaffProfile / AdministratorProfile id for Team Lead edit modal. */
    staffProfileId?: number;
    // City support
    cityId?: number;
    cityName?: string;
    /** USPS-normalized ZIP from primary address (GET athletes/me) */
    postalCode?: string;
    /** State label from address snapshot (may duplicate legacy `location`) */
    stateName?: string;
    instagramHandle?: string;
    tiktokHandle?: string;
    linkedinUrl?: string;
    facebookName?: string;
    hudlUrl?: string;
    xHandle?: string;
    parentUserId?: number;
    cleverId?: string | null;
    /** Clever district display name when known (GET athletes/me). */
    cleverDistrictName?: string | null;
    authProvider?: string;
    lastLoginTime?: string | null;
    staffMember?: StaffMemberData | null;
}

interface CoachData {
    id?: string;
    firstName?: string;
    lastName?: string;
    title?: string;
    email?: string;
    phone?: string;
    profileImageUrl?: string;
}

interface StaffMemberData {
    userId?: number;
    firstName?: string;
    lastName?: string;
    displayName?: string;
    email?: string;
    telephone?: string;
    title?: string;
    role?: string;
    profileImageUrl?: string;
}

export default function AthleteProfile() {
    const { athleteId } = useParams<{ athleteId?: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const { isAuthenticated, isLoading: authLoading } = useAuth();

    const isOwnProfileEarly = isAthlete() && !athleteId;
    const { avatarUrl: myAvatarUrl } = useMyAvatar(isOwnProfileEarly && getTermsAccepted());
    const [athleteData, setAthleteData] = useState<AthleteProfileData | null>(null);
    const [coachData, setCoachData] = useState<CoachData | null>(null);
    const [loading, setLoading] = useState(true);
    const [, setSilentRefreshing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const [dateOfBirthText, setDateOfBirthText] = useState("");
    const [isSocialEditMode, setIsSocialEditMode] = useState(false);
    const [editData, setEditData] = useState<AthleteProfileData>({});
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [socialValidationErrors, setSocialValidationErrors] = useState<{
        instagramHandle?: string;
        tiktokHandle?: string;
        linkedinUrl?: string;
        facebookName?: string;
        hudlUrl?: string;
        xHandle?: string;
    }>({});
    const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
    const [isShareModalOpen, setIsShareModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState<string | null>(null);
    const [showAdminEditAthleteModal, setShowAdminEditAthleteModal] = useState(false);
    const [teamLeadHeaderRefresh, setTeamLeadHeaderRefresh] = useState(0);
    const [requirePasswordChange] = useState(
        localStorage.getItem('requirePasswordChange') === 'true' ||
        (location.state as { requirePasswordChange?: boolean } | null)?.requirePasswordChange === true
    );

    // Check if user is a guardian viewing in read-only mode (impersonating an athlete)
    const guardianViewingAthleteId = getGuardianViewingAthleteId();
    const readOnly = isGuardian() || Boolean(guardianViewingAthleteId);
    const canSelfEdit = canAthleteSelfEditProfile();



    // Get team member info from navigation state
    const navState = location.state as AthleteAdminNavState | null;

    const userIdForAdmin = localStorage.getItem("userId");
    const isAdminViewingAthlete = !isAthlete() && !!athleteId && athleteId !== userIdForAdmin;

    const athleteBreadcrumbName = useAdminAthleteDisplayName(athleteId, navState?.athleteDisplayName);
    const { staffId: athleteStaffId, staffName: athleteStaffName } = useAthleteStaffForAdminBreadcrumb(
        athleteId,
        Boolean(isAdminViewingAthlete && athleteId)
    );

    const handleNavigateToTeamMember = useCallback(() => {
        navigateToTeamMemberFromBreadcrumb(
            navigate,
            navState?.teamMemberId,
            athleteData?.staffId ?? athleteStaffId ?? null
        );
    }, [navigate, navState?.teamMemberId, athleteData?.staffId, athleteStaffId]);

    const canEditTeamLeadOnProfile =
        (isAdmin() || isStaff() || isPolaris()) && isAdminViewingAthlete;

    // Function to fetch athlete data (reusable for refresh after uploads)
    const fetchAthleteData = useCallback(async (silent = false): Promise<AthleteProfileData | null> => {

        try {
            if (silent) {
                setSilentRefreshing(true);
            } else {
                setLoading(true);
            }
            setError(null);
            const profilePath = athleteProfileFetchPath(athleteId);
            if (!profilePath) {
                // Auth or guardian viewing id may still be hydrating — wait rather than call /me.
                setLoading(false);
                return null;
            }

            const apiUrl = buildApiUrl(profilePath) + `?t=${Date.now()}`;


            const athleteRes = await apiClient(apiUrl, {
                headers: { Accept: "application/json" },
                credentials: "include" // Use HTTP-only cookie for auth
            });

            if (athleteRes.status === 401) {
                // Redirect to login page when unauthorized
                window.location.href = '/login';
                return null;
            }

            if (athleteRes.ok) {
                const data: AthleteProfileData = await athleteRes.json();
                // Store athlete ID in localStorage for profile updates
                if (data.id) {
                    localStorage.setItem('athleteId', String(data.id));
                }

                setAthleteData(data);

                // Get team member info from navigation state if available
                if (navState?.teamMemberName) {
                    // Use the team member name from navigation state
                    const nameParts = navState.teamMemberName.split(' ');
                    setCoachData({
                        id: navState.teamMemberId,
                        firstName: nameParts[0] || navState.teamMemberName,
                        lastName: nameParts.slice(1).join(' ') || '',
                    });
                }
                return data;
            } else if (athleteRes.status === 403 && !athleteId) {
                const hasAcceptedTerms = getTermsAccepted();
                if (!hasAcceptedTerms) {
                    setAthleteData({});
                } else {
                    setError(`Failed to load athlete profile.`);
                }
            } else {
                console.error("API response not OK:", athleteRes.status, athleteRes.statusText);
                setError(`Failed to load athlete profile.`);
            }
        } catch (error) {
            console.error("Failed to fetch athlete data:", error);
            setError("Failed to load athlete profile");
        } finally {
            setLoading(false);
            setSilentRefreshing(false);
        }
        return null;
    }, [athleteId, navState?.teamMemberId, navState?.teamMemberName]);

    const handleAdminEditAthleteSaved = useCallback(() => {
        setShowAdminEditAthleteModal(false);
        void fetchAthleteData(true).then(() => {
            setTeamLeadHeaderRefresh((n) => n + 1);
        });
    }, [fetchAthleteData]);

    useEffect(() => {
        // Only fetch athlete data when auth is complete
        if (!authLoading && isAuthenticated) {
            fetchAthleteData();
        }
    }, [fetchAthleteData, authLoading, isAuthenticated]);

    // Check if terms have been accepted and show modal if not
    useEffect(() => {
        // Only show the terms modal when viewing own profile (not when admin viewing another athlete)
        if (!athleteId && !loading) {
            const hasAcceptedTerms = getTermsAccepted();
            if (!hasAcceptedTerms) {
                setIsTermsModalOpen(true);
            }
        }
    }, [athleteId, loading]);

    // Listen for termsAccepted event to refetch data after terms are accepted
    useEffect(() => {
        const handleTermsAccepted = () => {
            // Trigger a refetch by updating location.state dependency
            window.location.reload();
        };

        window.addEventListener('termsAccepted', handleTermsAccepted);
        return () => {
            window.removeEventListener('termsAccepted', handleTermsAccepted);
        };
    }, []);

    // Access-link / password-reset flows must land in edit mode with the password form visible.
    useEffect(() => {
        if (
            !requirePasswordChange ||
            !isAthlete() ||
            athleteId ||
            readOnly ||
            loading ||
            isEditMode ||
            !athleteData
        ) {
            return;
        }

        setEditData({ ...athleteData });
        setDateOfBirthText(formatDateOfBirthUs(athleteData.dateOfBirth) ?? "");
        setIsEditMode(true);
        setSaveError(null);
    }, [
        requirePasswordChange,
        athleteId,
        readOnly,
        loading,
        isEditMode,
        athleteData,
    ]);

    // Handler for opening edit mode
    const handleEditProfile = async () => {
        if (readOnly || !canAthleteSelfEditProfile()) return;
        const freshData = await fetchAthleteData(true);
        const profileData = freshData ?? athleteData;
        if (profileData) {
            setEditData({ ...profileData });
            setDateOfBirthText(formatDateOfBirthUs(profileData.dateOfBirth) ?? "");
            setIsEditMode(true);
            setSaveError(null);
        }
    };

    // Handler for delete account
    const handleDeleteAccount = async () => {
        setIsDeleting(true);
        setDeleteError(null);

        try {
            const apiUrl = buildApiUrl('api/athletes/me');
            const response = await apiClient(apiUrl, {
                method: 'DELETE',
                credentials: 'include',
                headers: {
                    'Accept': 'application/json'
                }
            });

            if (response.status === 204) {
                // Success - clear all auth data and redirect to login
                localStorage.clear();
                sessionStorage.clear();
                window.location.href = '/login';
            } else if (response.status === 400) {
                setDeleteError('Unable to delete account. You may not have the correct permissions.');
                setIsDeleting(false);
            } else if (response.status === 401) {
                setDeleteError('You must be logged in to delete your account.');
                setIsDeleting(false);
            } else if (response.status === 404) {
                setDeleteError('Account not found.');
                setIsDeleting(false);
            } else {
                setDeleteError('Failed to delete account. Please try again.');
                setIsDeleting(false);
            }
        } catch (error) {
            setDeleteError('An unexpected error occurred. Please try again.');
            setIsDeleting(false);
        }
    };

    // Handler for canceling edit
    const handleCancelEdit = () => {
        if (requirePasswordChange) {
            return;
        }
        setIsEditMode(false);
        setEditData({});
        setDateOfBirthText("");
        setSaveError(null);
    };

    // Handler for saving edited profile
    const handleSaveProfile = async () => {
        if (!editData) return;

        setSaving(true);
        setSaveError(null);

        try {
            const dobTrim = dateOfBirthText.trim();
            let resolvedDobIso = "";
            if (dobTrim) {
                const parsed = parseDateOfBirthInputToIso(dobTrim);
                if (!parsed) {
                    setSaveError("Date of birth: use MM/DD/YYYY.");
                    return;
                }
                if (!isIsoDateStrictlyBeforeToday(parsed)) {
                    setSaveError("Date of birth must be in the past.");
                    return;
                }
                resolvedDobIso = parsed;
            }

            // Get the athlete ID to use - either from URL param or stored in localStorage
            const targetAthleteId = athleteId || athleteData?.id || localStorage.getItem('athleteId');

            if (!targetAthleteId) {
                throw new Error("No athlete ID available for profile update");
            }

            // Helper function to get sport object or create from string
            const getSportObject = (sportData?: Sport | string) => {
                if (!sportData) return null;
                if (typeof sportData === 'object' && 'id' in sportData) {
                    return sportData;
                }
                // If it's a string, we can't send it as object without the ID
                return null;
            };

            // Build the request payload according to API spec
            const payload = {
                firstName: editData.firstName || athleteData?.firstName || "",
                lastName: editData.lastName || athleteData?.lastName || "",
                email: editData.email || athleteData?.email || "",
                title: editData.nickname || athleteData?.nickname || "",
                state: editData.location || athleteData?.location || "",
                stateId: athleteData?.stateId, // Include if available from original data
                cityId: athleteData?.cityId, // Include if available
                staffName: athleteData?.staffName || "", // Include if available from original data
                staffId: athleteData?.staffId, // Include if available from original data
                nickname: editData.nickname || athleteData?.nickname || "",
                graduationYear: editData.graduationYear ? parseInt(editData.graduationYear) : (athleteData?.graduationYear ? parseInt(athleteData.graduationYear) : undefined),
                school: editData.school || athleteData?.school || "",
                program: editData.program || (typeof editData.sport === 'string' ? editData.sport : '') || athleteData?.program || (typeof athleteData?.sport === 'string' ? athleteData.sport : '') || "",
                sport: getSportObject(editData.sport) ?? getSportObject(athleteData?.sport) ?? null,
                location: editData.location || athleteData?.location || "",
                postalCode: editData.postalCode || athleteData?.postalCode || "",
                pronouns: editData.pronouns || athleteData?.pronouns || "",
                dateOfBirth: resolvedDobIso,
                athletePhone: editData.phone || editData.athletePhone || athleteData?.phone || athleteData?.athletePhone || "",
                parentEmail: editData.parentEmail || athleteData?.parentEmail || "",
                parentEmail2: editData.parentEmail2 || athleteData?.parentEmail2 || "",
                parentPhone: editData.parentPhone || editData.parentMobile || athleteData?.parentPhone || athleteData?.parentMobile || "",
                // Social handles saved via handleSaveSocial only — omit here to avoid clearing media kit socials.
                parentUserId: athleteData?.parentUserId || localStorage.getItem('userId'),
            };

            // Use the utility function that correctly calls PUT /api/athletes/{athleteId}/profile
            const result = await updateAthleteProfile(targetAthleteId, payload, isAthlete());

            if (result.success) {
                // Refresh the athlete profile data
                await fetchAthleteData(true);
                // Notify the layout to refresh the banner with a slight delay to ensure backend processing
                setTimeout(() => {
                    window.dispatchEvent(new Event('athleteProfileUpdated'));
                    window.dispatchEvent(new CustomEvent('profilePictureUpdated'));
                }, 500); // 500ms delay to ensure backend has processed
                if (!requirePasswordChange) {
                    setIsEditMode(false);
                    setEditData({});
                    setDateOfBirthText("");
                }
            } else {
                throw new Error(result.error || "Failed to save profile");
            }
        } catch (err: any) {
            console.error("Failed to save profile:", err);
            setSaveError(err?.message || "Failed to save profile");
        } finally {
            setSaving(false);
        }
    };


    // Handler for form field changes
    const handleFieldChange = (field: keyof AthleteProfileData, value: string) => {
        setEditData(prev => ({ ...prev, [field]: value }));

        // Validate social media fields
        if (field === 'instagramHandle') {
            const validation = validateInstagramHandle(value);
            setSocialValidationErrors(prev => ({
                ...prev,
                instagramHandle: validation.isValid ? undefined : validation.errorMessage
            }));
        } else if (field === 'tiktokHandle') {
            const validation = validateTikTokHandle(value);
            setSocialValidationErrors(prev => ({
                ...prev,
                tiktokHandle: validation.isValid ? undefined : validation.errorMessage
            }));
        } else if (field === 'linkedinUrl') {
            const validation = validateLinkedInUrl(value);
            setSocialValidationErrors(prev => ({
                ...prev,
                linkedinUrl: validation.isValid ? undefined : validation.errorMessage
            }));
        } else if (field === 'facebookName') {
            const validation = validateFacebookName(value);
            setSocialValidationErrors(prev => ({
                ...prev,
                facebookName: validation.isValid ? undefined : validation.errorMessage
            }));
        } else if (field === 'hudlUrl') {
            const validation = validateHudlUrl(value);
            setSocialValidationErrors(prev => ({
                ...prev,
                hudlUrl: validation.isValid ? undefined : validation.errorMessage
            }));
        } else if (field === 'xHandle') {
            const validation = validateXHandle(value);
            setSocialValidationErrors(prev => ({
                ...prev,
                xHandle: validation.isValid ? undefined : validation.errorMessage
            }));
        }
    };

    // Handler for opening social media edit mode
    const handleEditSocial = () => {
        if (readOnly) return; // Prevent editing in read-only mode
        const userId = localStorage.getItem("userId");
        const isAdminViewingAthlete = !isAthlete() && athleteId && athleteId !== userId;
        if (isAdminViewingAthlete) return; // Prevent editing when admin/staff viewing athlete
        if (athleteData) {
            setEditData({ ...athleteData });
        }
        setIsSocialEditMode(true);
    };

    // Handler for saving social media links
    const handleSaveSocial = async () => {
        if (!editData) return;

        // Check for validation errors
        const hasErrors = Object.values(socialValidationErrors).some(error => error !== undefined);
        if (hasErrors) {
            setSaveError("Please fix validation errors before saving");
            return;
        }

        setSaving(true);
        setSaveError(null);

        try {
            // Get the athlete ID to use - either from URL param or stored in localStorage
            const targetAthleteId = athleteId || athleteData?.id || localStorage.getItem('athleteId');

            if (!targetAthleteId) {
                throw new Error("No athlete ID available for profile update");
            }

            // Helper function to get sport object or create from string
            const getSportObject = (sportData?: Sport | string) => {
                if (!sportData) return null;
                if (typeof sportData === 'object' && 'id' in sportData) {
                    return sportData;
                }
                // If it's a string, we can't send it as object without the ID
                return null;
            };

            const normalizedSocials = normalizeAtHandlesInSocials({
                instagramHandle: editData.instagramHandle ?? "",
                tiktokHandle: editData.tiktokHandle ?? "",
                linkedinUrl: editData.linkedinUrl ?? "",
                facebookName: editData.facebookName ?? "",
                hudlUrl: editData.hudlUrl ?? "",
                xHandle: editData.xHandle ?? "",
            });

            // Build the complete request payload according to API spec
            const payload = {
                firstName: editData.firstName || athleteData?.firstName || "",
                lastName: editData.lastName || athleteData?.lastName || "",
                email: editData.email || athleteData?.email || "",
                title: editData.nickname || athleteData?.nickname || "",
                state: editData.location || athleteData?.location || "",
                stateId: athleteData?.stateId, // Include if available
                cityId: athleteData?.cityId, // Include if available
                staffName: athleteData?.staffName || "",
                staffId: athleteData?.staffId,
                nickname: editData.nickname || athleteData?.nickname || "",
                graduationYear: editData.graduationYear ? parseInt(editData.graduationYear) : (athleteData?.graduationYear ? parseInt(athleteData.graduationYear) : undefined),
                school: editData.school || athleteData?.school || "",
                program: editData.program || (typeof editData.sport === 'string' ? editData.sport : '') || athleteData?.program || (typeof athleteData?.sport === 'string' ? athleteData.sport : '') || "",
                sport: getSportObject(editData.sport) ?? getSportObject(athleteData?.sport) ?? null,
                location: editData.location || athleteData?.location || "",
                postalCode: editData.postalCode || athleteData?.postalCode || "",
                pronouns: editData.pronouns || athleteData?.pronouns || "",
                dateOfBirth: editData.dateOfBirth || athleteData?.dateOfBirth || "",
                athletePhone: editData.phone || editData.athletePhone || athleteData?.phone || athleteData?.athletePhone || "",
                parentEmail: editData.parentEmail || athleteData?.parentEmail || "",
                parentEmail2: editData.parentEmail2 || athleteData?.parentEmail2 || "",
                parentPhone: editData.parentPhone || editData.parentMobile || athleteData?.parentPhone || athleteData?.parentMobile || "",
                instagramHandle: normalizedSocials.instagramHandle,
                tiktokHandle: normalizedSocials.tiktokHandle,
                linkedinUrl: normalizedSocials.linkedinUrl,
                facebookName: normalizedSocials.facebookName,
                hudlUrl: normalizedSocials.hudlUrl,
                xHandle: normalizedSocials.xHandle,
                // For admin updates, parentUserId is the admin's userId from localStorage
                parentUserId: athleteData?.parentUserId || localStorage.getItem('userId'),
            };

            // Use the utility function that correctly calls PUT /api/athletes/{athleteId}/profile
            const result = await updateAthleteProfile(targetAthleteId, payload, isAthlete());

            if (result.success) {
                // Refresh the athlete profile data
                await fetchAthleteData(true);
                // Notify the layout to refresh the banner with a slight delay to ensure backend processing
                setTimeout(() => {
                    window.dispatchEvent(new Event('athleteProfileUpdated'));
                    window.dispatchEvent(new CustomEvent('profilePictureUpdated'));
                }, 500); // 500ms delay to ensure backend has processed
                setIsSocialEditMode(false);
                setEditData({});
            } else {
                throw new Error(result.error || "Failed to save profile");
            }
        } catch (err: any) {
            console.error("Failed to save social media links:", err);
            setSaveError(err?.message || "Failed to save profile");
        } finally {
            setSaving(false);
        }
    };

    // Handler for canceling social media edit
    const handleCancelSocialEdit = () => {
        setIsSocialEditMode(false);
        setEditData({});
        setSaveError(null);
        setSocialValidationErrors({});
    };


    if (loading || authLoading) {
        if (isAdminViewingAthlete) {
            const teamMemberBreadcrumbLabel = getTeamMemberBreadcrumbLabel(
                coachData,
                navState?.teamMemberName,
                athleteStaffName ?? undefined
            );
            return (
                <div className="athlete-profile-wrapper">
                    <div className="athlete-profile-header-top">
                        <AthleteAdminBreadcrumbNav
                            teamMemberLabel={teamMemberBreadcrumbLabel}
                            onTeamMemberClick={handleNavigateToTeamMember}
                            athleteLabel={athleteBreadcrumbName}
                            className="breadcrumbs-above-layout"
                        />
                    </div>
                    <div className="athlete-profile-container">
                        <div className="loading-spinner">Loading...</div>
                    </div>
                </div>
            );
        }
        return (
            <div className="athlete-profile-container">
                <div className="loading-spinner">Loading...</div>
            </div>
        );
    }

    // If terms haven't been accepted and modal is open, show a minimal loading state
    // The terms modal will overlay on top
    if (isTermsModalOpen && !athleteId && !getTermsAccepted()) {
        return (
            <div className="athlete-profile-wrapper">
                <div className="athlete-profile-container">
                    <div className="loading-spinner">Please accept terms to continue...</div>
                </div>
                <TermsAndConditionsModal
                    open={isTermsModalOpen}
                    onClose={() => setIsTermsModalOpen(false)}
                />
            </div>
        );
    }

    // Only show error if we've finished loading and still don't have data
    if (!loading && (error || !athleteData)) {
        if (isAdminViewingAthlete) {
            const teamMemberBreadcrumbLabel = getTeamMemberBreadcrumbLabel(
                coachData,
                navState?.teamMemberName,
                athleteStaffName ?? undefined
            );
            return (
                <div className="athlete-profile-wrapper">
                    <div className="athlete-profile-header-top">
                        <AthleteAdminBreadcrumbNav
                            teamMemberLabel={teamMemberBreadcrumbLabel}
                            onTeamMemberClick={handleNavigateToTeamMember}
                            athleteLabel={athleteBreadcrumbName}
                            className="breadcrumbs-above-layout"
                        />
                    </div>
                    <div className="athlete-profile-container">
                        <div className="error-message">{error || "Athlete not found"}</div>
                    </div>
                </div>
            );
        }
        return (
            <div className="athlete-profile-wrapper">
                <GuardianAthleteSelector />
                <div className="athlete-profile-container">
                    <div className="error-message">{error || "Athlete not found"}</div>
                </div>
            </div>
        );
    }

    // Ensure athleteData is not null before accessing properties
    if (!athleteData) {
        return null;
    }

    const displayName = `${athleteData.firstName || ''} ${athleteData.lastName || ''}`.trim() || 'Athlete';
    const isCleverAdminStaffViewingCleverAthlete =
        !isAthlete() &&
        !isGuardian() &&
        Boolean(athleteId) &&
        athleteData.authProvider === 'CLEVER';
    const teamMemberBreadcrumbLabel = getTeamMemberBreadcrumbLabel(
        coachData,
        navState?.teamMemberName,
        athleteData.staffName ?? athleteStaffName ?? undefined
    );
    const graduationYear = athleteData.graduationYear || '';
    // Only show sport in the badge, don't fallback to program
    // Extract sport name from object or use string directly
    const sport = typeof athleteData.sport === 'object' && athleteData.sport?.name
        ? athleteData.sport.name
        : (typeof athleteData.sport === 'string' ? athleteData.sport : '');
    // Check if athlete is viewing their own profile and in edit mode
    const isAthleteEditingOwnProfile = isAthlete() && !athleteId && isEditMode;

    // Helper: render a read-only value with "Not provided" styling for empty values
    const isOwnProfile = isAthlete() && !athleteId;
    const canFetchAdminProfile = isAdmin() || isStaff() || isCoach() || isPolaris();
    const staffMemberUserId = navState?.teamMemberId || athleteData?.staffId;

    const renderValue = (value: string | undefined | null) => {
        if ((isOwnProfile || isAdminViewingAthlete || isCleverAdminStaffViewingCleverAthlete) && (!value || value === '-')) {
            return <span className="profile-value--empty">Not provided</span>;
        }
        return <span>{value || '-'}</span>;
    };

    return (
        <div className="athlete-profile-wrapper">
            {requirePasswordChange && isOwnProfile && (
                <div
                    className="alert alert-warning"
                    data-testid="password-change-required-banner"
                    style={{
                        backgroundColor: '#fff3cd',
                        border: '1px solid #ffc107',
                        borderRadius: '8px',
                        padding: '16px',
                        marginBottom: '20px',
                        color: '#856404',
                    }}
                >
                    <strong>Password Change Required</strong>
                    <p style={{ margin: '8px 0 0 0' }}>
                        You must change your password before accessing other parts of the application.
                        Please update your password in the form below.
                    </p>
                </div>
            )}

            {/* Breadcrumb + Share button row for admin/staff viewing athlete */}
            {isAdminViewingAthlete && (
                <div className="athlete-profile-header-top">
                    <AthleteAdminBreadcrumbNav
                        teamMemberLabel={teamMemberBreadcrumbLabel}
                        onTeamMemberClick={handleNavigateToTeamMember}
                        athleteLabel={athleteData.nickname || displayName}
                        className="breadcrumbs-above-layout"
                    />
                    {PROFILE_SHARING_UI_ENABLED && (
                        <button
                            className="share-button"
                            onClick={() => setIsShareModalOpen(true)}
                            data-testid="share-profile-button"
                        >
                            Share
                        </button>
                    )}
                </div>
            )}

            {/* Share button for athlete viewing own profile */}
            {PROFILE_SHARING_UI_ENABLED && isAthlete() && !athleteId && !readOnly && !isAdminViewingAthlete && (
                <div className="athlete-profile-header-top">
                    <button
                        className="share-button"
                        onClick={() => setIsShareModalOpen(true)}
                        data-testid="share-profile-button"
                    >
                        Share
                    </button>
                </div>
            )}

            {/* Guardian Athlete Selector - shown above profile for guardians with multiple athletes */}
            <GuardianAthleteSelector />

            <div className="athlete-profile-container">
                {/* If athlete is editing their own profile, show edit form instead of profile card */}
                {isAthleteEditingOwnProfile ? (
                    <div className="athlete-main-profile">
                        <AthleteEditForm
                            athleteData={athleteData}
                            onSave={async () => {
                                // Refresh the athlete profile data
                                await fetchAthleteData(true);
                                // Notify the layout to refresh the banner with a slight delay
                                setTimeout(() => {
                                    window.dispatchEvent(new Event('athleteProfileUpdated'));
                                    window.dispatchEvent(new CustomEvent('profilePictureUpdated'));
                                }, 500);
                                if (!requirePasswordChange) {
                                    setIsEditMode(false);
                                }
                            }}
                            onCancel={requirePasswordChange ? undefined : handleCancelEdit}
                            saving={saving}
                        />

                        <PasswordForm
                            saveUrl={buildApiUrl("api/auth/password")}
                            useCookies={true}
                            skipCurrentPassword={requirePasswordChange}
                            onChanged={async () => {
                                await logoutAfterPasswordChange();
                            }}
                        />

                        {/* Social Section - Still visible during edit */}
                        <div className="social-section">
                            <div className="section-header">
                                <h3>Social</h3>
                                {!isSocialEditMode && !readOnly && !isAdminViewingAthlete ? (
                                    <button className="edit-section-button" onClick={handleEditSocial}>
                                        <img src={EditIcon} alt="Edit" />
                                    </button>
                                ) : isSocialEditMode ? (
                                    <div className="edit-actions">
                                        <button
                                            className="btn-cancel"
                                            onClick={handleCancelSocialEdit}
                                            disabled={saving}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            className="btn-save"
                                            onClick={handleSaveSocial}
                                            disabled={saving}
                                        >
                                            {saving ? 'Saving...' : 'Save'}
                                        </button>
                                    </div>
                                ) : null}
                            </div>
                            <div className="social-links">
                                <div className="social-item">
                                    <div className="social-icon instagram">📷</div>
                                    <div className="social-info">
                                        <span className="social-platform">Instagram</span>
                                        {isSocialEditMode ? (
                                            <>
                                                <input
                                                    type="text"
                                                    className="edit-input social-input"
                                                    placeholder="@username"
                                                    value={editData.instagramHandle || ''}
                                                    onChange={(e) => handleFieldChange('instagramHandle', e.target.value)}
                                                />
                                                {socialValidationErrors.instagramHandle && (
                                                    <span className="social-validation-error">{socialValidationErrors.instagramHandle}</span>
                                                )}
                                            </>
                                        ) : (
                                            <>
                                                {athleteData.instagramHandle ? (
                                                    <a
                                                        href={getInstagramUrl(athleteData.instagramHandle) || '#'}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="social-handle social-link"
                                                        data-testid="instagram-link"
                                                    >
                                                        {formatAtHandleDisplay(athleteData.instagramHandle)}
                                                    </a>
                                                ) : (
                                                    <span className="social-handle">-</span>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                                <div className="social-item">
                                    <div className="social-icon tiktok">🎵</div>
                                    <div className="social-info">
                                        <span className="social-platform">TikTok</span>
                                        {isSocialEditMode ? (
                                            <>
                                                <input
                                                    type="text"
                                                    className="edit-input social-input"
                                                    placeholder="@username"
                                                    value={editData.tiktokHandle || ''}
                                                    onChange={(e) => handleFieldChange('tiktokHandle', e.target.value)}
                                                />
                                                {socialValidationErrors.tiktokHandle && (
                                                    <span className="social-validation-error">{socialValidationErrors.tiktokHandle}</span>
                                                )}
                                            </>
                                        ) : (
                                            <>
                                                {athleteData.tiktokHandle ? (
                                                    <a
                                                        href={getTikTokUrl(athleteData.tiktokHandle) || '#'}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="social-handle social-link"
                                                        data-testid="tiktok-link"
                                                    >
                                                        {formatAtHandleDisplay(athleteData.tiktokHandle)}
                                                    </a>
                                                ) : (
                                                    <span className="social-handle">-</span>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                                <div className="social-item">
                                    <div className="social-icon linkedin">💼</div>
                                    <div className="social-info">
                                        <span className="social-platform">LinkedIn</span>
                                        {isSocialEditMode ? (
                                            <>
                                                <input
                                                    type="text"
                                                    className="edit-input social-input"
                                                    placeholder="linkedin.com/in/username"
                                                    value={editData.linkedinUrl || ''}
                                                    onChange={(e) => handleFieldChange('linkedinUrl', e.target.value)}
                                                />
                                                {socialValidationErrors.linkedinUrl && (
                                                    <span className="social-validation-error">{socialValidationErrors.linkedinUrl}</span>
                                                )}
                                            </>
                                        ) : (
                                            <>
                                                {athleteData.linkedinUrl ? (
                                                    <a
                                                        href={getLinkedInUrl(athleteData.linkedinUrl) || '#'}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="social-handle social-link"
                                                        data-testid="linkedin-link"
                                                    >
                                                        {athleteData.linkedinUrl}
                                                    </a>
                                                ) : (
                                                    <span className="social-handle">-</span>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                                <div className="social-item">
                                    <div className="social-icon facebook">f</div>
                                    <div className="social-info">
                                        <span className="social-platform">Facebook</span>
                                        {isSocialEditMode ? (
                                            <>
                                                <input
                                                    type="text"
                                                    className="edit-input social-input"
                                                    placeholder="Full Name"
                                                    value={editData.facebookName || ''}
                                                    onChange={(e) => handleFieldChange('facebookName', e.target.value)}
                                                />
                                                {socialValidationErrors.facebookName && (
                                                    <span className="social-validation-error">{socialValidationErrors.facebookName}</span>
                                                )}
                                            </>
                                        ) : (
                                            <>
                                                {athleteData.facebookName ? (
                                                    <a
                                                        href={getFacebookUrl(athleteData.facebookName) || '#'}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="social-handle social-link"
                                                        data-testid="facebook-link"
                                                    >
                                                        {athleteData.facebookName}
                                                    </a>
                                                ) : (
                                                    <span className="social-handle">-</span>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                                <div className="social-item">
                                    <div className="social-icon hudl">🏆</div>
                                    <div className="social-info">
                                        <span className="social-platform">Hudl</span>
                                        {isSocialEditMode ? (
                                            <>
                                                <input
                                                    type="text"
                                                    className="edit-input social-input"
                                                    placeholder="hudl.com/profile/123"
                                                    value={editData.hudlUrl || ''}
                                                    onChange={(e) => handleFieldChange('hudlUrl', e.target.value)}
                                                />
                                                {socialValidationErrors.hudlUrl && (
                                                    <span className="social-validation-error">{socialValidationErrors.hudlUrl}</span>
                                                )}
                                            </>
                                        ) : (
                                            <>
                                                {athleteData.hudlUrl ? (
                                                    <a
                                                        href={getHudlUrl(athleteData.hudlUrl) || '#'}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="social-handle social-link"
                                                        data-testid="hudl-link"
                                                    >
                                                        {athleteData.hudlUrl}
                                                    </a>
                                                ) : (
                                                    <span className="social-handle">-</span>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                                <div className="social-item">
                                    <div className="social-icon x-twitter">𝕏</div>
                                    <div className="social-info">
                                        <span className="social-platform">X</span>
                                        {isSocialEditMode ? (
                                            <>
                                                <input
                                                    type="text"
                                                    className="edit-input social-input"
                                                    placeholder="@username"
                                                    value={editData.xHandle || ''}
                                                    onChange={(e) => handleFieldChange('xHandle', e.target.value)}
                                                />
                                                {socialValidationErrors.xHandle && (
                                                    <span className="social-validation-error">{socialValidationErrors.xHandle}</span>
                                                )}
                                            </>
                                        ) : (
                                            <>
                                                {athleteData.xHandle ? (
                                                    <a
                                                        href={getXUrl(athleteData.xHandle) || '#'}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="social-handle social-link"
                                                        data-testid="x-link"
                                                    >
                                                        {formatAtHandleDisplay(athleteData.xHandle)}
                                                    </a>
                                                ) : (
                                                    <span className="social-handle">-</span>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <>
                        {/* Breadcrumb Navigation - Hidden for athletes viewing their own profile */}
                        {/* Removed per requirement: Athletes should not see breadcrumbs */}

                        {/* Athlete Status Card - Only visible to admin/staff users */}
                        {/*{isAdminViewingAthlete && (*/}
                        {/*     <div className="athlete-status-card">*/}
                        {/*<div className="athlete-status-info">*/}
                        {/*    <div className="athlete-status-icon">⚠️</div>*/}
                        {/*    <span className="athlete-name">{displayName}</span>*/}
                        {/*</div>*/}
                        {/*{!athleteData.hasSignedIn && athleteData.email && (*/}
                        {/*    <div className="athlete-status-message">*/}
                        {/*        <span>This athlete has not signed in yet, resend the login email to </span>*/}
                        {/*        <strong>{athleteData.email}</strong>*/}
                        {/*        <button className="resend-button">*/}
                        {/*            Resend*/}
                        {/*        </button>*/}
                        {/*    </div>*/}
                        {/*)}*/}
                        {/*</div>*/}
                        {/*)}*/}

                        {/* Save Error Message */}
                        {saveError && (
                            <div className="error-message" style={{ marginBottom: '1rem', height: 'auto', padding: '12px' }}>
                                {saveError}
                            </div>
                        )}

                        {/* Main Profile Section */}
                        <div className="athlete-main-profile">
                            <div className={`athlete-profile-card${(isAthlete() && !athleteId) || isAdminViewingAthlete || isCleverAdminStaffViewingCleverAthlete ? ' athlete-own-profile' : ''}`}>
                                <div className={`athlete-card-header${isAdminViewingAthlete ? ' athlete-card-header--admin-view' : ''}`}>
                                    <div className="athlete-card-info">
                                        <div className="athlete-avatar">
                                            <ProfileAvatar
                                                displayName={displayName}
                                                size="large"
                                                className="athlete-avatar-img"
                                                alt={displayName}
                                                allowUpload={false}
                                                userId={isAdminViewingAthlete && athleteData?.userId && (athleteData?.imageUrl || athleteData?.profileImageUrl) ? athleteData.userId : undefined}
                                                imageUrl={athleteData?.imageUrl || athleteData?.profileImageUrl}
                                                previewUrl={isOwnProfile ? (myAvatarUrl ?? undefined) : undefined}
                                                skipAvatarFetch={true}
                                            />
                                        </div>
                                        <div className="athlete-details">
                                            <h2 className="athlete-name-large">{displayName}</h2>
                                            {sport && (
                                                <span className="athlete-sport-tag">{sport}</span>
                                            )}
                                        </div>
                                    </div>
                                    {isAdminViewingAthlete && (
                                        <div className="athlete-header-last-login" data-testid="athlete-last-login">
                                            <span className="athlete-header-last-login-label">Last login</span>
                                            <span className="athlete-header-last-login-value">
                                                {formatLastLoginDateTime(athleteData.lastLoginTime)}
                                            </span>
                                        </div>
                                    )}
                                    {canSelfEdit && !athleteId && !isEditMode ? (
                                        <button
                                            type="button"
                                            className="tm-profile-edit"
                                            aria-label="Edit profile"
                                            title="Edit profile"
                                            onClick={handleEditProfile}>
                                            <img src={EditIcon} alt="Edit" />
                                        </button>
                                    ) : canSelfEdit && !athleteId && isEditMode ? (
                                        <div className="edit-actions">
                                            {!requirePasswordChange ? (
                                                <button
                                                    className="btn-cancel"
                                                    onClick={handleCancelEdit}
                                                    disabled={saving}
                                                    data-testid="cancel-profile-edit"
                                                >
                                                    Cancel
                                                </button>
                                            ) : null}
                                            <button
                                                className="btn-save"
                                                onClick={handleSaveProfile}
                                                disabled={saving}
                                            >
                                                {saving ? 'Saving...' : 'Save'}
                                            </button>
                                        </div>
                                    ) : null}
                                </div>

                                <div className="athlete-info-grid">
                                    {/* Personal Information Section (Clever hint + district live here; no Clever user ID) */}
                                    <div className="athlete-profile-section-content">
                                        <div className="athlete-profile-personal-heading">
                                            <div className="athlete-profile-section-header section-personal">
                                                <span className="section-icon">👤</span>
                                                <span>Personal Information</span>
                                            </div>
                                            {athleteData.authProvider === 'CLEVER' && (
                                                <p className="clever-account-hint clever-account-hint--under-personal-header">
                                                    This Polaris account uses Clever sign-in. Your district name below helps
                                                    confirm you are with the correct school organization.
                                                </p>
                                            )}
                                        </div>

                                        <div className="info-row info-row--personal-four">
                                            <div className="info-item">
                                                <label>First Name</label>
                                                {isEditMode ? (
                                                    <input
                                                        type="text"
                                                        className="edit-input"
                                                        value={editData.firstName || ''}
                                                        onChange={(e) => handleFieldChange('firstName', e.target.value)}
                                                    />
                                                ) : renderValue(athleteData.firstName)}
                                            </div>
                                            <div className="info-item">
                                                <label>Last Name</label>
                                                {isEditMode ? (
                                                    <input
                                                        type="text"
                                                        className="edit-input"
                                                        value={editData.lastName || ''}
                                                        onChange={(e) => handleFieldChange('lastName', e.target.value)}
                                                    />
                                                ) : renderValue(athleteData.lastName)}
                                            </div>
                                            <div className="info-item">
                                                <label>Date of Birth</label>
                                                {isEditMode ? (
                                                    <input
                                                        type="text"
                                                        inputMode="numeric"
                                                        autoComplete="bday"
                                                        placeholder="MM/DD/YYYY"
                                                        className="edit-input"
                                                        value={dateOfBirthText}
                                                        onChange={(e) => setDateOfBirthText(autoFormatDateInput(e.target.value, dateOfBirthText))}
                                                        onBlur={() => {
                                                            const trimmed = dateOfBirthText.trim();
                                                            if (!trimmed) {
                                                                setEditData((prev) => ({ ...prev, dateOfBirth: "" }));
                                                                setDateOfBirthText("");
                                                                return;
                                                            }
                                                            const iso = parseDateOfBirthInputToIso(trimmed);
                                                            if (iso) {
                                                                setEditData((prev) => ({ ...prev, dateOfBirth: iso }));
                                                                setDateOfBirthText(formatDateOfBirthUs(iso) ?? "");
                                                            }
                                                        }}
                                                    />
                                                ) : renderValue(formatDateOfBirthUs(athleteData.dateOfBirth))}
                                            </div>
                                            <div className="info-item">
                                                <label>Nickname</label>
                                                {isEditMode ? (
                                                    <input
                                                        type="text"
                                                        className="edit-input"
                                                        value={editData.nickname || ''}
                                                        onChange={(e) => handleFieldChange('nickname', e.target.value)}
                                                    />
                                                ) : renderValue(athleteData.nickname)}
                                            </div>
                                        </div>

                                        {athleteData.authProvider === 'CLEVER' && (
                                            <div className="info-row info-row--personal-four">
                                                <div className="info-item">
                                                    <label>District name</label>
                                                    {renderValue(athleteData.cleverDistrictName)}
                                                </div>
                                                <div
                                                    className="info-item info-item--location-align-spacer"
                                                    aria-hidden="true"
                                                />
                                                <div
                                                    className="info-item info-item--location-align-spacer"
                                                    aria-hidden="true"
                                                />
                                                <div
                                                    className="info-item info-item--location-align-spacer"
                                                    aria-hidden="true"
                                                />
                                            </div>
                                        )}
                                    </div>

                                    {/* Location Section */}
                                    <div className="athlete-profile-section-content">
                                        <div className="athlete-profile-section-header section-location">
                                            <span className="section-icon">📍</span>
                                            <span>Location</span>
                                        </div>

                                        <div className="info-row">
                                            {SHOW_ATHLETE_PROFILE_ZIP_FIELD && (
                                                <div className="info-item">
                                                    <label>ZIP code</label>
                                                    {isEditMode ? (
                                                        <input
                                                            type="text"
                                                            className="edit-input"
                                                            value={editData.postalCode || ''}
                                                            onChange={(e) => handleFieldChange('postalCode', e.target.value.replace(/\D/g, '').slice(0, 5))}
                                                        />
                                                    ) : renderValue(athleteData.postalCode)}
                                                </div>
                                            )}
                                            <div className="info-item">
                                                <label>City</label>
                                                {isEditMode ? (
                                                    <input
                                                        type="text"
                                                        className="edit-input"
                                                        value={editData.cityName || ''}
                                                        onChange={(e) => handleFieldChange('cityName', e.target.value)}
                                                    />
                                                ) : renderValue(athleteData.cityName)}
                                            </div>
                                            <div className="info-item">
                                                <label>State</label>
                                                {isEditMode ? (
                                                    <input
                                                        type="text"
                                                        className="edit-input"
                                                        value={editData.location || ''}
                                                        onChange={(e) => handleFieldChange('location', e.target.value)}
                                                    />
                                                ) : renderValue(athleteData.location || athleteData.stateName)}
                                            </div>
                                            {/* Fourth column aligns Location with Personal (4 equal columns) */}
                                            <div
                                                className="info-item info-item--location-align-spacer"
                                                aria-hidden="true"
                                            />
                                        </div>
                                    </div>

                                    {/* Academic & Sport Section */}
                                    <div className="athlete-profile-section-content">
                                        <div className="athlete-profile-section-header section-academic">
                                            <span className="section-icon">🏆</span>
                                            <span>Academic & Sport</span>
                                        </div>

                                        <div className="info-row">
                                            <div className="info-item">
                                                <label>School</label>
                                                {isEditMode ? (
                                                    <input
                                                        type="text"
                                                        className="edit-input"
                                                        value={editData.school || ''}
                                                        onChange={(e) => handleFieldChange('school', e.target.value)}
                                                    />
                                                ) : renderValue(athleteData.school)}
                                            </div>
                                            <div className="info-item">
                                                <label>Graduation Year</label>
                                                {isEditMode ? (
                                                    <input
                                                        type="text"
                                                        className="edit-input"
                                                        value={editData.graduationYear || ''}
                                                        onChange={(e) => handleFieldChange('graduationYear', e.target.value)}
                                                    />
                                                ) : renderValue(graduationYear)}
                                            </div>
                                            <div className="info-item">
                                                <label>Sport</label>
                                                {isEditMode ? (
                                                    <input
                                                        type="text"
                                                        className="edit-input"
                                                        value={typeof editData.sport === 'object' ? editData.sport.name : (editData.sport || '')}
                                                        onChange={(e) => handleFieldChange('sport', e.target.value)}
                                                    />
                                                ) : renderValue(sport)}
                                            </div>
                                            <div className="info-item">
                                                <label>Program / Club</label>
                                                {isEditMode ? (
                                                    <input
                                                        type="text"
                                                        className="edit-input"
                                                        value={editData.program || (typeof editData.sport === 'object' ? editData.sport.name : editData.sport) || ''}
                                                        onChange={(e) => handleFieldChange('program', e.target.value)}
                                                    />
                                                ) : renderValue(athleteData.program || sport)}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Athlete Contact Section */}
                                    <div className="athlete-profile-section-content">
                                        <div className="athlete-profile-section-header section-contact">
                                            <span className="section-icon">📱</span>
                                            <span>Athlete Contact</span>
                                        </div>

                                        <div className="info-row">
                                            <div className="info-item">
                                                <label>Email</label>
                                                {isEditMode ? (
                                                    <input
                                                        type="email"
                                                        className="edit-input"
                                                        value={editData.email || ''}
                                                        onChange={(e) => handleFieldChange('email', e.target.value)}
                                                    />
                                                ) : renderValue(athleteData.email)}
                                            </div>
                                            <div className="info-item">
                                                <label>Mobile</label>
                                                {isEditMode ? (
                                                    <input
                                                        type="tel"
                                                        className="edit-input"
                                                        value={editData.phone || editData.athletePhone || ''}
                                                        onChange={(e) => handleFieldChange('phone', e.target.value)}
                                                    />
                                                ) : renderValue(athleteData.phone || athleteData.athletePhone)}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Parent / Guardian Contact Section */}
                                    <div className="athlete-profile-section-content">
                                        <div className="athlete-profile-section-header section-parent">
                                            <span className="section-icon">👥</span>
                                            <span>Parent / Guardian Contact</span>
                                        </div>

                                        <div className="info-row">
                                            <div className="info-item">
                                                <label>Email</label>
                                                {isEditMode ? (
                                                    <input
                                                        type="email"
                                                        className="edit-input"
                                                        value={editData.parentEmail || ''}
                                                        disabled
                                                        readOnly
                                                    />
                                                ) : renderValue(athleteData.parentEmail)}
                                            </div>
                                            <div className="info-item">
                                                <label>Email 2</label>
                                                {isEditMode ? (
                                                    <input
                                                        type="email"
                                                        className="edit-input"
                                                        value={editData.parentEmail2 || ''}
                                                        disabled
                                                        readOnly
                                                    />
                                                ) : renderValue(athleteData.parentEmail2)}
                                            </div>
                                        </div>
                                        <div className="info-row">
                                            <div className="info-item">
                                                <label>Mobile</label>
                                                {isEditMode ? (
                                                    <input
                                                        type="tel"
                                                        className="edit-input"
                                                        value={editData.parentPhone || editData.parentMobile || ''}
                                                        onChange={(e) => handleFieldChange('parentPhone', e.target.value)}
                                                    />
                                                ) : renderValue(athleteData.parentPhone || athleteData.parentMobile)}
                                            </div>
                                            <div className="info-item">
                                                {/* Empty to maintain grid layout */}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Delete Account Button - Only visible in edit mode for athlete */}
                                    {isEditMode && isAthlete() && !athleteId && (
                                        <div className="info-row">
                                            <div className="info-item">
                                                <button
                                                    type="button"
                                                    className="athlete-delete-account-btn"
                                                    onClick={() => setIsDeleteModalOpen(true)}
                                                    disabled={saving}
                                                >
                                                    Delete Account
                                                </button>
                                            </div>
                                            <div className="info-item">
                                                {/* Empty to maintain grid layout */}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Social Section */}
                            <div className="social-section">
                                <div className="section-header">
                                    <h3>Social</h3>
                                    {!isSocialEditMode && !readOnly && !isAdminViewingAthlete ? (
                                        <button className="edit-section-button" onClick={handleEditSocial}>
                                            <img src={EditIcon} alt="Edit" />
                                        </button>
                                    ) : isSocialEditMode ? (
                                        <div className="edit-actions">
                                            <button
                                                className="btn-cancel"
                                                onClick={handleCancelSocialEdit}
                                                disabled={saving}
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                className="btn-save"
                                                onClick={handleSaveSocial}
                                                disabled={saving}
                                            >
                                                {saving ? 'Saving...' : 'Save'}
                                            </button>
                                        </div>
                                    ) : null}
                                </div>
                                <div className="social-links">
                                    <div className="social-item">
                                        <div className="social-icon instagram">📷</div>
                                        <div className="social-info">
                                            <span className="social-platform">Instagram</span>
                                            {isSocialEditMode ? (
                                                <>
                                                    <input
                                                        type="text"
                                                        className="edit-input social-input"
                                                        placeholder="@username"
                                                        value={editData.instagramHandle || ''}
                                                        onChange={(e) => handleFieldChange('instagramHandle', e.target.value)}
                                                    />
                                                    {socialValidationErrors.instagramHandle && (
                                                        <span className="social-validation-error">{socialValidationErrors.instagramHandle}</span>
                                                    )}
                                                </>
                                            ) : (
                                                <>
                                                    {athleteData.instagramHandle ? (
                                                        <a
                                                            href={getInstagramUrl(athleteData.instagramHandle) || '#'}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="social-handle social-link"
                                                            data-testid="instagram-link-main"
                                                        >
                                                            {formatAtHandleDisplay(athleteData.instagramHandle)}
                                                        </a>
                                                    ) : (
                                                        <span className="social-handle">-</span>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    <div className="social-item">
                                        <div className="social-icon tiktok">🎵</div>
                                        <div className="social-info">
                                            <span className="social-platform">TikTok</span>
                                            {isSocialEditMode ? (
                                                <>
                                                    <input
                                                        type="text"
                                                        className="edit-input social-input"
                                                        placeholder="@username"
                                                        value={editData.tiktokHandle || ''}
                                                        onChange={(e) => handleFieldChange('tiktokHandle', e.target.value)}
                                                    />
                                                    {socialValidationErrors.tiktokHandle && (
                                                        <span className="social-validation-error">{socialValidationErrors.tiktokHandle}</span>
                                                    )}
                                                </>
                                            ) : (
                                                <>
                                                    {athleteData.tiktokHandle ? (
                                                        <a
                                                            href={getTikTokUrl(athleteData.tiktokHandle) || '#'}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="social-handle social-link"
                                                            data-testid="tiktok-link-main"
                                                        >
                                                            {formatAtHandleDisplay(athleteData.tiktokHandle)}
                                                        </a>
                                                    ) : (
                                                        <span className="social-handle">-</span>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    <div className="social-item">
                                        <div className="social-icon linkedin">💼</div>
                                        <div className="social-info">
                                            <span className="social-platform">LinkedIn</span>
                                            {isSocialEditMode ? (
                                                <>
                                                    <input
                                                        type="text"
                                                        className="edit-input social-input"
                                                        placeholder="linkedin.com/in/username"
                                                        value={editData.linkedinUrl || ''}
                                                        onChange={(e) => handleFieldChange('linkedinUrl', e.target.value)}
                                                    />
                                                    {socialValidationErrors.linkedinUrl && (
                                                        <span className="social-validation-error">{socialValidationErrors.linkedinUrl}</span>
                                                    )}
                                                </>
                                            ) : (
                                                <>
                                                    {athleteData.linkedinUrl ? (
                                                        <a
                                                            href={getLinkedInUrl(athleteData.linkedinUrl) || '#'}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="social-handle social-link"
                                                            data-testid="linkedin-link-main"
                                                        >
                                                            {athleteData.linkedinUrl}
                                                        </a>
                                                    ) : (
                                                        <span className="social-handle">-</span>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    <div className="social-item">
                                        <div className="social-icon facebook">f</div>
                                        <div className="social-info">
                                            <span className="social-platform">Facebook</span>
                                            {isSocialEditMode ? (
                                                <>
                                                    <input
                                                        type="text"
                                                        className="edit-input social-input"
                                                        placeholder="Full Name"
                                                        value={editData.facebookName || ''}
                                                        onChange={(e) => handleFieldChange('facebookName', e.target.value)}
                                                    />
                                                    {socialValidationErrors.facebookName && (
                                                        <span className="social-validation-error">{socialValidationErrors.facebookName}</span>
                                                    )}
                                                </>
                                            ) : (
                                                <>
                                                    {athleteData.facebookName ? (
                                                        <a
                                                            href={getFacebookUrl(athleteData.facebookName) || '#'}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="social-handle social-link"
                                                            data-testid="facebook-link-main"
                                                        >
                                                            {athleteData.facebookName}
                                                        </a>
                                                    ) : (
                                                        <span className="social-handle">-</span>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    <div className="social-item">
                                        <div className="social-icon hudl">🏆</div>
                                        <div className="social-info">
                                            <span className="social-platform">Hudl</span>
                                            {isSocialEditMode ? (
                                                <>
                                                    <input
                                                        type="text"
                                                        className="edit-input social-input"
                                                        placeholder="hudl.com/profile/123"
                                                        value={editData.hudlUrl || ''}
                                                        onChange={(e) => handleFieldChange('hudlUrl', e.target.value)}
                                                    />
                                                    {socialValidationErrors.hudlUrl && (
                                                        <span className="social-validation-error">{socialValidationErrors.hudlUrl}</span>
                                                    )}
                                                </>
                                            ) : (
                                                <>
                                                    {athleteData.hudlUrl ? (
                                                        <a
                                                            href={getHudlUrl(athleteData.hudlUrl) || '#'}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="social-handle social-link"
                                                            data-testid="hudl-link-main"
                                                        >
                                                            {athleteData.hudlUrl}
                                                        </a>
                                                    ) : (
                                                        <span className="social-handle">-</span>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    <div className="social-item">
                                        <div className="social-icon x-twitter">𝕏</div>
                                        <div className="social-info">
                                            <span className="social-platform">X</span>
                                            {isSocialEditMode ? (
                                                <>
                                                    <input
                                                        type="text"
                                                        className="edit-input social-input"
                                                        placeholder="@username"
                                                        value={editData.xHandle || ''}
                                                        onChange={(e) => handleFieldChange('xHandle', e.target.value)}
                                                    />
                                                    {socialValidationErrors.xHandle && (
                                                        <span className="social-validation-error">{socialValidationErrors.xHandle}</span>
                                                    )}
                                                </>
                                            ) : (
                                                <>
                                                    {athleteData.xHandle ? (
                                                        <a
                                                            href={getXUrl(athleteData.xHandle) || '#'}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="social-handle social-link"
                                                            data-testid="x-link-main"
                                                        >
                                                            {formatAtHandleDisplay(athleteData.xHandle)}
                                                        </a>
                                                    ) : (
                                                        <span className="social-handle">-</span>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <AthleteTeamLeadSection
                                canEditTeamLead={canEditTeamLeadOnProfile}
                                canFetchAdminProfile={canFetchAdminProfile}
                                staffMemberUserId={staffMemberUserId}
                                staffMember={athleteData?.staffMember}
                                refreshTrigger={teamLeadHeaderRefresh}
                                onEditAthlete={() => setShowAdminEditAthleteModal(true)}
                            />
                        </div>
                    </>
                )}
            </div>

            {/* Terms and Conditions Modal */}
            <TermsAndConditionsModal
                open={isTermsModalOpen}
                onClose={() => setIsTermsModalOpen(false)}
            />

            {/* Share Profile Modal */}
            {PROFILE_SHARING_UI_ENABLED && isShareModalOpen && (
                <ShareProfileModal
                    open={isShareModalOpen}
                    onClose={() => setIsShareModalOpen(false)}
                    athleteUserId={athleteData?.userId?.toString() || localStorage.getItem('userId') || undefined}
                />
            )}

            {/* Delete Account Modal */}
            <AthleteDeleteAccountModal
                open={isDeleteModalOpen}
                onClose={() => {
                    if (!isDeleting) {
                        setIsDeleteModalOpen(false);
                        setDeleteError(null);
                    }
                }}
                onConfirm={handleDeleteAccount}
                loading={isDeleting}
                error={deleteError}
            />

            {/* Admin/staff Edit athlete (Team Lead assignment) — reuse directory modal */}
            {canEditTeamLeadOnProfile && (
                <AddAthleteProfileModal
                    open={showAdminEditAthleteModal}
                    onClose={() => setShowAdminEditAthleteModal(false)}
                    onSaved={handleAdminEditAthleteSaved}
                    initial={
                        athleteData
                            ? {
                                  id: athleteData.id,
                                  userId: athleteData.userId,
                                  firstName: athleteData.firstName || "",
                                  lastName: athleteData.lastName || "",
                                  email: athleteData.email || "",
                                  parentEmail: athleteData.parentEmail || "",
                                  parentEmail2: athleteData.parentEmail2 || "",
                                  state: athleteData.stateName || athleteData.location || "",
                                  stateId: athleteData.stateId,
                                  cityId: athleteData.cityId,
                                  cityName: athleteData.cityName,
                                  staffName:
                                      athleteData.staffName ||
                                      athleteData.staffMember?.displayName ||
                                      "",
                                  // Prefer profile id for Team Lead select; never pass user id as staffId
                                  staffId: athleteData.staffProfileId,
                              }
                            : undefined
                    }
                />
            )}
        </div>
    );
}
